import { useCallback, useEffect, useRef, useState } from 'react';
import { store } from '../state/store';
import type { Note } from '../storage/schemas';

type Content = Pick<Note, 'lines' | 'images' | 'strokes' | 'style' | 'designId'>;

const pick = (n: Note): Content => ({ lines: n.lines, images: n.images, strokes: n.strokes, style: n.style, designId: n.designId });

const LIMIT = 200;
const COALESCE_MS = 1200;

/**
 * Undoable editing for one note. Every change goes through `edit`; consecutive changes with the same
 * `group` within ~1 s are merged into one undo step (typing a word is one step, not one per letter).
 */
export function useNoteEditing(noteId: string) {
  const undo = useRef<Content[]>([]);
  const redo = useRef<Content[]>([]);
  const last = useRef<{ group: string; at: number } | null>(null);
  const [, force] = useState(0);

  useEffect(() => {
    undo.current = [];
    redo.current = [];
    last.current = null;
    force((x) => x + 1);
  }, [noteId]);

  const edit = useCallback(
    (fn: (n: Note) => Note, group = 'misc') => {
      const cur = store.get().notes[noteId];
      if (!cur) return;
      const now = Date.now();
      const coalesce = last.current && last.current.group === group && group !== 'misc' && now - last.current.at < COALESCE_MS;
      if (!coalesce) {
        undo.current.push(pick(cur));
        if (undo.current.length > LIMIT) undo.current.shift();
        redo.current = [];
      }
      last.current = { group, at: now };
      store.updateNote(noteId, fn);
      force((x) => x + 1);
    },
    [noteId],
  );

  const apply = (from: Content[], to: Content[]) => {
    const cur = store.get().notes[noteId];
    const prev = from.pop();
    if (!cur || !prev) return;
    to.push(pick(cur));
    last.current = null;
    store.updateNote(noteId, (n) => ({ ...n, ...prev }));
    force((x) => x + 1);
  };

  return {
    edit,
    undo: () => apply(undo.current, redo.current),
    redo: () => apply(redo.current, undo.current),
    canUndo: undo.current.length > 0,
    canRedo: redo.current.length > 0,
  };
}

export type NoteEditing = ReturnType<typeof useNoteEditing>;
